import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Access a value in an object using a dot notation path
 * Example: getValueByAccessorKey(user, "profile.address.city")
 * 
 * @param obj The object to access properties from
 * @param accessorKey Dot notation path (e.g., "user.name", "address.street")
 * @param defaultValue Optional default value if path doesn't exist
 * @returns The value at the accessorKey path or defaultValue if not found
 */
export function getValueByAccessorKey<T = any>(
  obj: any, 
  accessorKey: string,
  defaultValue?: T
): T | undefined {
  if (!obj || !accessorKey) return defaultValue;
  
  const keys = accessorKey.split(".");
  const result = keys.reduce(
    (current: any, key: string) => 
      (current && typeof current === "object" ? current[key] : undefined),
    obj
  );
  
  return result !== undefined ? result : defaultValue;
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number
    sizeType?: "accurate" | "normal"
  } = {}
) {
  const { decimals = 0, sizeType = "normal" } = opts

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"]
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"]
  if (bytes === 0) return "0 Byte"
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${sizeType === "accurate"
      ? (accurateSizes[i] ?? "Bytes")
      : (sizes[i] ?? "Bytes")
    }`
}